import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FiCamera, FiFolder, FiShare2, FiSettings, FiX, FiHeart, FiDroplet, FiTag, FiLogOut, FiFilm, FiUsers, FiInbox, FiGrid, FiShield, FiArchive } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import useStripBadge from '../utils/useStripBadge';
import { getFeatureFlags } from '../utils/featureFlags';

export default function MobileNav() {
  const { user, logout, loadingMessage, isAuthenticating } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const features = getFeatureFlags(user);
  const { hasNew: hasStripNew, markSeen: markStripSeen } = useStripBadge(user?.id, { enabled: features.strip });

  useEffect(() => {
    if (features.strip && location.pathname.startsWith('/strip')) {
      markStripSeen();
    }
  }, [location.pathname, markStripSeen, features.strip]);
  useEffect(() => {
    document.body.classList.toggle('mobile-sheet-open', menuOpen);
    return () => document.body.classList.remove('mobile-sheet-open');
  }, [menuOpen]);
  const quickLinks = useMemo(() => {
    const links = [
      { to: '/photos', label: 'Photos', icon: FiCamera },
      { to: '/albums', label: 'Albums', icon: FiFolder },
      ...(features.strip ? [{ to: '/strip', label: 'The Strip', icon: FiFilm }] : []),
      ...(features.shares ? [{ to: '/shares', label: 'Shares', icon: FiShare2 }] : []),
      ...(features.clients ? [{ to: '/submissions', label: 'Clients', icon: FiInbox }] : []),
      { to: '/favorites', label: 'Favorites', icon: FiHeart },
      ...(features.friends ? [{ to: '/friends', label: 'Friends/Team', icon: FiUsers }] : []),
      { to: '/tags', label: 'Tags', icon: FiTag },
      { to: '/archive', label: 'Scan Archive', icon: FiArchive },
      { to: '/watermark', label: 'Watermark', icon: FiDroplet },
      { to: '/settings', label: 'Settings', icon: FiSettings }
    ];

    if (user?.role === 'super_admin') {
      links.push({ to: '/admin', label: 'Admin', icon: FiShield });
    }

    return links;
  }, [user?.role, features.strip, features.shares, features.clients, features.friends, features.vault]);

  return (
    <>
      {/* Mobile Bottom Tab Bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 flex items-center justify-around bg-[#fdf8e2] border-t border-[#efe4b6]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)'}}>
        <NavLink to="/photos" className={({ isActive }) => `flex flex-col items-center flex-1 gap-1 py-3 text-xs ${isActive ? 'text-[#304849] font-bold' : 'text-[#5f7270]'}`}>
          <FiCamera size={20} />
          <span>Photos</span>
        </NavLink>

        <NavLink to="/albums" className={({ isActive }) => `flex flex-col items-center flex-1 gap-1 py-3 text-xs ${isActive ? 'text-[#304849] font-bold' : 'text-[#5f7270]'}`}>
          <FiFolder size={20} />
          <span>Albums</span>
        </NavLink>

        {features.shares && (
          <NavLink to="/shares" className={({ isActive }) => `flex flex-col items-center flex-1 gap-1 py-3 text-xs ${isActive ? 'text-[#304849] font-bold' : 'text-[#5f7270]'}`}>
            <FiShare2 size={20} />
            <span>Shares</span>
          </NavLink>
        )}

        {features.clients && (
          <NavLink to="/submissions" className={({ isActive }) => `flex flex-col items-center flex-1 gap-1 py-3 text-xs ${isActive ? 'text-[#304849] font-bold' : 'text-[#5f7270]'}`}>
            <FiInbox size={20} />
            <span>Clients</span>
          </NavLink>
        )}

        <button className="flex flex-col items-center gap-1 px-3 py-3 text-xs text-[#5f7270]" onClick={() => setMenuOpen(true)} aria-label="Open menu">
          <FiGrid size={20} />
          <span>Menu</span>
        </button>
      </nav>

      {/* Bottom Sheet Menu */}
      {menuOpen && (
        <>
          <div className="mobile-sheet-backdrop" onClick={() => setMenuOpen(false)} />
          <div className="mobile-sheet" role="dialog" aria-modal="true">
            <div className="mobile-sheet-handle" />
            <div className="mobile-sheet-header">
              <div>
                <div className="mobile-sheet-title">Quick access</div>
                <div className="mobile-sheet-subtitle">Jump to everything else</div>
              </div>
              <button className="mobile-sheet-close" onClick={() => setMenuOpen(false)} aria-label="Close menu">
                <FiX size={22} />
              </button>
            </div>

            <div className="mobile-sheet-grid">
              {quickLinks.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setMenuOpen(false)}
                  className="mobile-sheet-link"
                >
                  <span className="mobile-sheet-icon">
                    <Icon size={18} />
                    {to === '/strip' && hasStripNew && (
                      <span className="strip-new-badge strip-new-badge--dot" aria-hidden="true" />
                    )}
                  </span>
                  <span>{label}</span>
                </NavLink>
              ))}
            </div>

            <button className="mobile-sheet-logout" onClick={logout} disabled={isAuthenticating}>
              {isAuthenticating ? (
                <span className="mobile-sheet-spinner" />
              ) : (
                <FiLogOut size={18} />
              )}
              {isAuthenticating ? (loadingMessage || 'Signing out...') : 'Sign out'}
            </button>
          </div>
        </>
      )}
    </>
  );
}
